import React, { useState } from "react";
import { motion } from "framer-motion";
import { Check, ArrowUpRight } from "lucide-react";
import { toast, Toaster } from "sonner";
import Layout from "../components/Layout";
import SEO from "../components/SEO";
import SectionHeading from "../components/SectionHeading";
import StatCounter from "../components/StatCounter";
import { IMAGES } from "../lib/images";
import { api, formatApiErrorDetail } from "../lib/api";

const AMENITIES = [
  { t: "Private Residences", b: "262 ultra-luxury residences with private elevators and full floor penthouses.", img: "luxury_condo_interior" },
  { t: "Five-Star Branded Hotel", b: "250 keys, destination dining, signature spa, and grand arrival sequence.", img: "luxury_hotel_lobby" },
  { t: "Branded Arrival", b: "Porte-cochère, private valet, and the signature ELLIOTT TOWERS entrance sequence — a five-star welcome at the heart of Downtown Tampa.", img: "branded_entrance" },
  { t: "Observation Deck", b: "Tampa's highest public observation experience — engineered for global recognition.", img: "observation_deck_view" },
  { t: "Curated Retail", b: "65,000 sq ft of high-end retail curated for the flagship promenade.", img: "retail_interior" },
  { t: "Class AAA Office", b: "100,000 sq ft of column-free institutional office.", img: "boardroom_office" },
  { t: "Chef's Kitchens", b: "Italian millwork, marble surfaces, and panoramic dining lounges in every residence.", img: "branded_kitchen" },
  { t: "Bayfront Great Room", b: "Floor-to-ceiling glass framing Bayshore sunsets — the social heart of every residence with panoramic water views.", img: "grand_living_room" },
  { t: "Spa & Wellness", b: "Stone-clad signature spa, hammam, cold plunge, and private wellness suites.", img: "luxury_amenity_spa" },
];

const FEATURES = [
  "Twin towers connected by a sky bridge",
  "262 ultra-luxury private residences",
  "Five-star branded hotel with destination dining",
  "Class AAA boutique office",
  "Curated luxury retail promenade",
  "Public observation deck — Tampa's highest",
  "On-site valet & private arrival sequence",
];

export default function ElliottTowersPage() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", company: "",
    investor_type: "institutional", investment_range: "5m_25m",
    project_interest: "Elliott Towers", message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/investor/inquiry", form);
      toast.success("Inquiry received. Our investor relations team will be in touch.");
      setForm((f) => ({ ...f, message: "" }));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <SEO title="Elliott Towers" description="Elliott Towers — a landmark ultra-luxury twin-tower mixed-use development planned for Downtown Tampa. Branded residences, five-star hotel, Class AAA office, public observation deck." path="/elliott-towers" image={IMAGES.elliott_twin_towers} />
      <Toaster theme="dark" position="bottom-right" />
      {/* HERO */}
      <section className="relative h-[100vh] min-h-[700px] overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.elliott_twin_towers} alt="Elliott Towers" className="w-full h-full object-cover scale-105" />
          <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-background/30 to-background" />
          <div className="absolute inset-0 bg-gradient-to-r from-background/60 via-transparent to-transparent" />
        </div>
        <div className="relative h-full max-w-[1600px] mx-auto px-6 lg:px-12 flex flex-col justify-end pb-20 z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.3 }} className="overline mb-6">
            — The flagship · Downtown Tampa
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.1, delay: 0.4, ease: [0.16, 1, 0.3, 1] }} className="font-serif text-6xl sm:text-7xl md:text-9xl lg:text-[11rem] leading-[0.92] font-light tracking-[-0.02em]">
            Elliott <em className="italic text-primary">Towers</em>
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.9, delay: 0.7 }} className="mt-8 text-lg md:text-xl text-foreground/80 max-w-2xl leading-relaxed">
              A twin-tower mixed-use landmark engineered to become Tampa's
              tallest tower — and the standard for ultra-luxury vertical
              urbanism in the American South.
          </motion.p>
        </div>
        <div className="absolute bottom-8 left-6 lg:left-12 z-10 font-mono text-xs text-foreground/40">
          27.9479° N · 82.4584° W · DOWNTOWN TAMPA
        </div>
      </section>

      {/* STATS */}
      <section className="border-y border-primary/10 py-20 px-6 lg:px-12 bg-card">
        <div className="max-w-[1600px] mx-auto grid grid-cols-2 lg:grid-cols-6 gap-10">
          <StatCounter value={740} suffix=" ft" label="Height" />
          <StatCounter value={62} label="Stories" />
          <StatCounter value={262} label="Residences" />
          <StatCounter value={250} label="Hotel Keys" />
          <StatCounter value={100000} suffix=" sf" label="Office" />
          <StatCounter value={65000} suffix=" sf" label="Retail" />
        </div>
      </section>

      {/* CONCEPT */}
      <section className="py-32 lg:py-44 px-6 lg:px-12">
        <div className="max-w-[1600px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline mb-6">— Concept</div>
            <h2 className="font-serif text-4xl md:text-6xl leading-[1.05] tracking-tight font-light text-balance">
              Two towers.<br />One vertical neighborhood.
            </h2>
          </div>
          <div className="lg:col-span-7">
            <p className="text-muted-foreground text-lg leading-relaxed">
              Elliott Towers introduces a transformational vertical neighborhood
              to the heart of Downtown Tampa. Twin towers — connected by an
              elevated sky bridge — host private residences, a five-star
              branded hotel, Class AAA office, curated retail,
              and a public observation deck engineered to become Tampa's
              highest.
            </p>
          </div>
        </div>
      </section>

      {/* AMENITIES GRID */}
      <section className="py-32 px-6 lg:px-12 bg-card border-y border-primary/10">
        <div className="max-w-[1600px] mx-auto">
          <SectionHeading overline="— Components" title="A complete vertical neighborhood." />
          <div className="mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {AMENITIES.map((a, i) => (
              <motion.div
                key={a.t}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                className="group"
              >
                <div className="aspect-[4/5] overflow-hidden bg-background">
                  <img src={IMAGES[a.img]} alt={a.t} className="w-full h-full object-cover img-grayscale-hover" />
                </div>
                <div className="mt-5">
                  <div className="font-mono text-primary text-xs tracking-widest">0{i + 1}</div>
                  <h3 className="mt-2 font-serif text-2xl">{a.t}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{a.b}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES LIST */}
      <section className="py-32 lg:py-44 px-6 lg:px-12">
        <div className="max-w-[1600px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline mb-6">— Specifications</div>
            <h2 className="font-serif text-4xl md:text-6xl leading-[1.05] tracking-tight font-light">
              Engineered to landmark.
            </h2>
            <div className="mt-10 aspect-square overflow-hidden">
              <img src={IMAGES.lobby_elevators} alt="Lobby with elevators" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="lg:col-span-7">
            <ul className="space-y-5">
              {FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-4 pb-5 border-b border-primary/10">
                  <Check className="w-4 h-4 text-primary mt-1.5 flex-shrink-0" />
                  <span className="font-serif text-xl md:text-2xl font-light">{f}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* INVESTOR INQUIRY */}
      <section id="inquire" className="py-32 px-6 lg:px-12 bg-card border-y border-primary/10">
        <div className="max-w-[1600px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <div className="overline mb-6">— Investor inquiry</div>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight font-light">
              Be part of a generational landmark.
            </h2>
            <p className="mt-6 text-muted-foreground leading-relaxed">
              Elliott Towers is open to qualified institutional capital partners,
              family offices, and strategic investors. Submit an inquiry to
              receive a confidential project briefing.
            </p>
          </div>
          <div className="lg:col-span-7">
            <form onSubmit={submit} data-testid="towers-investor-form" className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <input data-testid="towers-input-name" required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-background border border-input px-5 py-4 text-foreground focus:border-primary focus:outline-none transition-colors" />
              <input data-testid="towers-input-email" required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-background border border-input px-5 py-4 text-foreground focus:border-primary focus:outline-none transition-colors" />
              <input data-testid="towers-input-phone" placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-background border border-input px-5 py-4 text-foreground focus:border-primary focus:outline-none transition-colors" />
              <input data-testid="towers-input-company" placeholder="Firm / Family Office" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="bg-background border border-input px-5 py-4 text-foreground focus:border-primary focus:outline-none transition-colors" />
              <select data-testid="towers-input-investor-type" value={form.investor_type} onChange={(e) => setForm({ ...form, investor_type: e.target.value })} className="bg-background border border-input px-5 py-4 text-foreground focus:border-primary focus:outline-none transition-colors">
                <option value="individual">Individual / Accredited</option>
                <option value="institutional">Institutional Fund</option>
                <option value="family_office">Family Office</option>
                <option value="fund">REIT / Private Fund</option>
                <option value="developer">Developer / Strategic</option>
              </select>
              <select data-testid="towers-input-investment-range" value={form.investment_range} onChange={(e) => setForm({ ...form, investment_range: e.target.value })} className="bg-background border border-input px-5 py-4 text-foreground focus:border-primary focus:outline-none transition-colors">
                <option value="under_1m">Under $1M</option>
                <option value="1m_5m">$1M – $5M</option>
                <option value="5m_25m">$5M – $25M</option>
                <option value="25m_100m">$25M – $100M</option>
                <option value="100m_plus">$100M+</option>
              </select>
              <textarea data-testid="towers-input-message" required placeholder="Message" rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="md:col-span-2 bg-background border border-input px-5 py-4 text-foreground focus:border-primary focus:outline-none transition-colors resize-none" />
              <button data-testid="towers-submit-investor" disabled={submitting} type="submit" className="md:col-span-2 group inline-flex items-center justify-center gap-3 bg-primary text-primary-foreground px-8 py-5 text-[13px] uppercase tracking-[0.22em] hover:bg-primary/90 transition-all disabled:opacity-60">
                {submitting ? "Submitting…" : "Submit Confidential Inquiry"}
                <ArrowUpRight className="w-4 h-4" />
              </button>
            </form>
          </div>
        </div>
      </section>
    </Layout>
  );
}
